import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommentFilterMode } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Ban, Trash2 } from 'lucide-react';
import { Check, Loader2 } from '@/components/icons';
import { adminApi } from './api';

const MODES: { id: CommentFilterMode; label: string; desc: string }[] = [
  { id: 'mask', label: 'Mask', desc: 'Replace bad words with **** and post anyway.' },
  { id: 'block', label: 'Block', desc: "Reject the comment with a 'clean it up' message." },
  { id: 'hold', label: 'Hold & block', desc: 'Block it, queue it here; approving it emails the author.' },
];

function Toggle({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className={cn('relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50', on ? 'bg-brand' : 'bg-surface-raised')}
    >
      <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all', on ? 'left-[22px]' : 'left-0.5')} />
    </button>
  );
}

function FilterSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-comment-settings'], queryFn: adminApi.commentSettings });
  const [mode, setMode] = useState<CommentFilterMode>('mask');
  const [words, setWords] = useState('');
  useEffect(() => {
    if (data) {
      setMode(data.mode);
      setWords(data.blockedWords);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => adminApi.setCommentSettings(mode, words),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-comment-settings'] }),
  });

  return (
    <section className="card p-5">
      <h3 className="font-display text-base font-bold text-ink">Auto-filter</h3>
      <p className="mt-1 text-sm text-ink-muted">When a comment contains a blocked word (on shows with the filter on):</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              'rounded-xl border p-3 text-left transition',
              mode === m.id ? 'border-brand bg-brand/10' : 'border-border hover:border-border-strong',
            )}
          >
            <p className="text-sm font-semibold text-ink">{m.label}</p>
            <p className="mt-0.5 text-xs text-ink-faint">{m.desc}</p>
          </button>
        ))}
      </div>
      <label className="mt-4 block text-sm font-medium text-ink-muted">Blocked words (comma or line separated)</label>
      <textarea
        value={words}
        onChange={(e) => setWords(e.target.value)}
        rows={3}
        className="mt-1.5 w-full resize-y rounded-xl border border-border bg-surface px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none"
      />
      <Button size="sm" className="mt-3" onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isSuccess ? 'Saved' : save.isPending ? 'Saving…' : 'Save filter settings'}
      </Button>
    </section>
  );
}

function ShowToggles() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin-comment-shows'], queryFn: adminApi.commentShows });
  const set = useMutation({
    mutationFn: ({ id, input }: { id: string; input: { commentsEnabled?: boolean; commentFilter?: boolean } }) =>
      adminApi.setShowComments(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-comment-shows'] }),
  });

  return (
    <section className="card p-5">
      <h3 className="font-display text-base font-bold text-ink">Per-show controls</h3>
      <div className="mt-3 space-y-2">
        {(data?.shows ?? []).map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-2.5 last:border-0">
            <span className="text-sm font-medium text-ink">{s.title}</span>
            <div className="flex items-center gap-5">
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                Comments
                <Toggle on={s.commentsEnabled} disabled={set.isPending} onClick={() => set.mutate({ id: s.id, input: { commentsEnabled: !s.commentsEnabled } })} />
              </label>
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                Auto-filter
                <Toggle on={s.commentFilter} disabled={set.isPending} onClick={() => set.mutate({ id: s.id, input: { commentFilter: !s.commentFilter } })} />
              </label>
            </div>
          </div>
        ))}
        {data && data.shows.length === 0 && <p className="text-sm text-ink-muted">No shows yet.</p>}
      </div>
    </section>
  );
}

function CommentList() {
  const qc = useQueryClient();
  const [heldOnly, setHeldOnly] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-comments'] });
  const { data, isLoading } = useQuery({
    queryKey: ['admin-comments', heldOnly],
    queryFn: () => adminApi.comments(heldOnly ? 'held' : undefined),
  });
  const approve = useMutation({ mutationFn: (id: string) => adminApi.approveComment(id), onSuccess: invalidate });
  const del = useMutation({ mutationFn: (id: string) => adminApi.deleteComment(id), onSuccess: invalidate });
  const ban = useMutation({ mutationFn: (userId: string) => adminApi.ban(userId, 'Comment policy violation'), onSuccess: invalidate });

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-base font-bold text-ink">Recent comments</h3>
        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <input type="checkbox" checked={heldOnly} onChange={(e) => setHeldOnly(e.target.checked)} className="h-4 w-4 accent-brand" />
          Held only
        </label>
      </div>
      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-ink-faint"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : (data?.comments ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">{heldOnly ? 'Nothing waiting for review.' : 'No comments yet.'}</p>
      ) : (
        <ul className="space-y-2.5">
          {data!.comments.map((c) => (
            <li key={c.id} className={cn('card p-4', c.status === 'held' && 'border-warning/40 bg-warning/5')}>
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-faint">
                <span className="font-semibold text-ink">{c.authorName}</span>
                <span>{c.authorEmail}</span>
                <span>· {c.showTitle}</span>
                {c.status === 'held' && <span className="rounded-full bg-warning/15 px-2 py-0.5 font-semibold uppercase text-warning">Held</span>}
              </div>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-ink-muted">{c.body}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.status === 'held' && (
                  <Button size="sm" variant="secondary" onClick={() => approve.mutate(c.id)} disabled={approve.isPending}>
                    <Check size={14} /> Approve &amp; notify
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => del.mutate(c.id)} disabled={del.isPending}>
                  <Trash2 size={14} /> Delete
                </Button>
                <button
                  type="button"
                  onClick={() => { if (window.confirm(`Suspend ${c.authorEmail}? They'll be signed out and blocked from logging in.`)) ban.mutate(c.userId); }}
                  disabled={ban.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-surface-raised hover:text-danger"
                >
                  <Ban size={14} /> Ban author
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function CommentsAdmin() {
  return (
    <div className="space-y-6">
      <FilterSettings />
      <ShowToggles />
      <CommentList />
    </div>
  );
}
